<template>
  <BaseWrapper
    :loading="
      loading ||
      loadingPayments ||
      deleteConfirm.loading ||
      loadingVerify ||
      uploadDocument.loading ||
      loadingEditPayment
    "
  >
    <div class="space-y-4">
      <BaseCard title="Pembayaran Saldo Kurang Bayar">
        <template #action>
          <BaseBadge
            :color="balanceTrx.data.attributes.paid ? 'green' : 'yellow'"
            >{{
              balanceTrx.data.attributes.paid ? 'Lunas' : 'Belum Lunas'
            }}</BaseBadge
          >
        </template>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <BaseInput
            disabled
            label="Tanggal"
            inset
            :value="balanceTrx.data.attributes.createdAt | formatDate"
          />
          <BaseInput
            disabled
            label="Kode Kantor"
            inset
            :value="
              getSingleIncluded(
                balanceTrx,
                balanceTrx.data.relationships.office.data.id
              )?.attributes?.code
            "
          />
          <BaseInput
            disabled
            label="Jumlah"
            inset
            :value="
              balanceTrx.data.attributes[
                trxType === 'topup_stockist_debt'
                  ? 'order_debt_balance_amount'
                  : 'debt_amount'
              ] | toCurrency
            "
          />
          <BaseInput
            disabled
            label="Deskripsi"
            inset
            :value="balanceTrx.data.attributes.description"
          />
        </div>
      </BaseCard>

      <CreatePaymentForm
        v-if="!balanceTrx.data.attributes.paid"
        :min-amount="
          balanceTrx.data.attributes[
            trxType === 'topup_stockist_debt'
              ? 'order_debt_balance_amount'
              : 'debt_amount'
          ]
        "
        :paid="balanceTrx.data.attributes.payment_amount"
        ref="createPaymentForm"
        @save="onSubmit"
      />

      <BaseCard title="Daftar Pembayaran">
        <Datatable
          compact
          :columns="[
            { id: 'payment_method', name: 'Metode Pembayaran' },
            { id: 'transfer_date', name: 'Tanggal Transfer' },
            { id: 'receipt_number', name: 'No. Struk' },
            { id: 'amount', name: 'Jumlah' },
            { id: 'action', name: 'Aksi' },
          ]"
        >
          <template #tbody="{ classes }">
            <tr v-for="payment in payments.data" :key="payment.id">
              <td :class="[classes.td]">
                <div class="flex items-center gap-2">
                  <p>{{ payment.attributes.payment_method_type }}</p>
                  <p v-if="payment.attributes.office_bank_name">
                    - {{ payment.attributes.office_bank_name }}
                  </p>
                  <template
                    v-if="
                      payment.attributes.payment_method_type === 'QRIS' ||
                      payment.attributes.payment_method_type ===
                        'Transfer Bank' ||
                      payment.attributes.payment_method_type === 'EDC'
                    "
                  >
                    <a
                      v-if="payment.attributes.document_url"
                      :href="payment.attributes.document_url"
                    >
                      <BaseButton size="sm" color="white">
                        <Icon icon="heroicons:arrow-down-tray" />
                        Download Bukti
                      </BaseButton>
                    </a>
                    <BaseButton
                      v-else
                      size="sm"
                      color="white"
                      @click="onOpenUploadDocument(payment)"
                    >
                      <Icon icon="heroicons:arrow-up-tray" />
                      Upload Bukti
                    </BaseButton>
                  </template>
                  <BaseButton
                    v-if="payment.attributes.payment_method_type === 'QRIS'"
                    color="white"
                    size="sm"
                    @click="onOpenPaymentQr(payment)"
                  >
                    <Icon icon="heroicons:qr-code-solid" class="h-4 w-4" />
                  </BaseButton>
                </div>
              </td>
              <td :class="classes.td">
                <BaseInput
                  v-if="payment.attributes.office_bank_type === 'bank'"
                  size="sm"
                  type="date"
                  :shadow="false"
                  :disabled="payment.attributes.verified"
                  v-model="payment.attributes.transfer_date"
                  @change="onEditPayment(payment)"
                />
                <p v-else>-</p>
              </td>
              <td :class="[classes.td]">
                <BaseInput
                  v-if="payment.attributes.payment_method_type === 'EDC'"
                  type="text"
                  size="sm"
                  :shadow="false"
                  placeholder="Masukkan no. struk"
                  v-model="payment.attributes.receipt_number"
                  :disabled="payment.attributes.verified"
                  @change="onEditPayment(payment)"
                />
                <p v-else>-</p>
              </td>
              <td :class="classes.td">
                <p>{{ payment.attributes.amount | toCurrency }}</p>
              </td>
              <td :class="classes.td">
                <div v-if="!payment.attributes.verified" class="flex gap-2">
                  <BaseButton size="sm" @click="onVerify(payment)">
                    <Icon icon="heroicons:check-circle-solid" />
                    Verifikasi
                  </BaseButton>
                  <BaseButton
                    size="sm"
                    color="danger"
                    @click="onOpenDeleteConfirm(payment)"
                  >
                    <Icon icon="heroicons:x-circle-solid" />
                    Hapus
                  </BaseButton>
                </div>
              </td>
            </tr>
          </template>
          <template #tfoot="{ classes }">
            <tr class="bg-gray-50">
              <td colspan="2"></td>
              <td :class="[classes.td, 'font-semibold']">Total Pembayaran</td>
              <td :class="classes.td">
                {{ balanceTrx.data.attributes.payment_amount | toCurrency }}
              </td>
              <td></td>
            </tr>
          </template>
        </Datatable>
      </BaseCard>
    </div>

    <BaseConfirm
      :visible="deleteConfirm.visible"
      icon-type="badge"
      title="Konfirmasi Hapus Pembayaran"
      message="Pembayaran yang dihapus tidak dapat dikembalikan kembali"
      confirm-text="Hapus"
      cancel-text="Batal"
      @confirmed="onConfirmDelete"
      @close="deleteConfirm.visible = false"
    />

    <BaseModal
      :showing="uploadDocument.visible"
      @close="uploadDocument.visible = false"
    >
      <base-card title="Bukti File" :with-style="false">
        <form class="space-y-4" @submit.prevent="onUploadDocument">
          <div>
            <base-input
              type="file"
              :shadow="false"
              fullwidth
              size="padding-md"
              label="Upload File Bukti"
              @change="onChangeUploadDocument"
            />
            <p class="mt-1 text-xs text-gray-500">Tipe file: jpg, png</p>
          </div>
          <div class="flex gap-2">
            <base-button>Upload</base-button>
            <base-button
              type="button"
              color="white"
              @click="uploadDocument.visible = false"
              >Tutup</base-button
            >
          </div>
        </form>
      </base-card>
    </BaseModal>

    <BaseQrisPaymentModal
      :visible="paymentQris.visible"
      :detail="paymentQris"
      @close="paymentQris.visible = false"
    />
  </BaseWrapper>
</template>

<script>
import BaseConfirm from '../../../../../components/base/BaseConfirm.vue';
import BaseModal from '../../../../../components/base/BaseModal.vue';
import BaseBadge from '../../../../../components/base/BaseBadge.vue';
import BaseQrisPaymentModal from '../../../../../components/base/BaseQrisPaymentModal.vue';
import CreatePaymentForm from '../../../../../components/payment/create-payment-form.vue';
import { requestMixin } from '../../../../../mixins/request/request';
import { mapActions } from 'vuex';

export default {
  props: {
    trxType: String,
  },
  mixins: [requestMixin],
  components: {
    CreatePaymentForm,
    BaseConfirm,
    BaseModal,
    BaseQrisPaymentModal,
    BaseBadge,
  },
  data() {
    return {
      balanceTrx: {
        data: {
          attributes: {
            createdAt: null,
            debt_amount: null,
            order_debt_balance_amount: null,
            description: null,
            payment_amount: null,
          },
          relationships: {
            office: {
              data: {
                id: null,
              },
            },
          },
        },
        included: [],
      },
      payments: {
        data: [],
      },
      officeBanks: {
        data: [],
      },
      deleteConfirm: {
        id: null,
        loading: false,
        paymentAmount: null,
        visible: false,
      },
      uploadDocument: {
        id: null,
        loading: false,
        file: null,
        visible: false,
      },
      paymentQris: {
        visible: false,
        qris: null,
        price: null,
        qris_merchant: null,
        qris_city: null,
        bank_account_name: null,
        bank_account_number: null,
        orderCode: null,
      },
      loading: false,
      loadingPayments: false,
      loadingVerify: false,
      loadingEditPayment: false,
    };
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async loadBalanceTrx() {
      this.loading = true;

      const [res, err] = await this.request(
        `/api/v1/balance-trxes/${this.$route.params.id}`,
        {
          params: {
            include: 'office',
            'fields[balance-trxes]':
              'office,createdAt,order_debt_balance_amount,debt_amount,payment_amount,description,code,paid',
            'fields[offices]': 'code',
            'sort': '-id',
          },
        }
      );

      if (!err) {
        this.balanceTrx = res;
      }

      this.loading = false;
    },
    async loadPayments() {
      this.loadingPayments = true;

      const [res, err] = await this.request(
        `/api/v1/balance-trxes/${this.$route.params.id}/payments`,
        {
          params: {
            'fields[balance-trx-payments]':
              'payment_method_type,office_bank_name,office_bank_type,verified,receipt_number,transfer_date,amount,document_url,qris,qris_merchant,qris_city,office_bank_account_name,office_bank_account_number',
          },
        }
      );

      if (!err) {
        this.payments = res;
      }

      this.loadingPayments = false;
    },
    async onSubmit(paymentMethods) {
      this.loading = true;

      const [res, err] = await this.request(
        `/api/v1/balance-trxes/${this.balanceTrx.data.id}/-actions/add-payments`,
        {
          method: 'POST',
          data: {
            payment_methods: paymentMethods.map((pm) => ({
              amount: Number(pm.amount.replace(/\D/g, '')),
              payment_method_type_id: pm.paymentMethodTypeId,
              office_bank_id: pm.officeBankId,
            })),
          },
        }
      );

      if (!err) {
        this.createAlert({
          data: 'Pembayaran berhasil dibuat',
          status: 'success',
        });

        this.loadPayments();

        this.balanceTrx.data.attributes.payment_amount =
          res.data.attributes.payment_amount;

        this.$refs.createPaymentForm.reset();
      } else {
        this.createAlert({
          data: 'Gagal membuat pembayaran',
          detail: this.getRequestErrorMessage(err),
          status: 'error',
          type: 'confirm',
          autoClose: false,
        });
      }

      this.loading = false;
    },
    onOpenDeleteConfirm(payment) {
      this.deleteConfirm.id = payment.id;
      this.deleteConfirm.paymentAmount = payment.attributes.amount;
      this.deleteConfirm.visible = true;
    },
    async onConfirmDelete() {
      this.deleteConfirm.loading = true;

      const [, err] = await this.request(
        `/api/v1/balance-trx-payments/${this.deleteConfirm.id}`,
        {
          method: 'delete',
        }
      );

      if (err) {
        this.deleteConfirm.loading = false;

        this.createAlert({
          data: 'Gagal menghapus pembayaran',
          detail: this.getRequestErrorMessage(err),
          status: 'error',
        });
      } else {
        this.deleteConfirm.loading = false;
        this.deleteConfirm.visible = false;

        this.createAlert({
          data: 'Pembayaran berhasil dihapus',
          status: 'success',
        });

        this.loadPayments();

        this.balanceTrx.data.attributes.payment_amount -=
          this.deleteConfirm.paymentAmount;
      }
    },
    async onVerify(payment) {
      this.loadingVerify = true;

      const [res, err] = await this.request(
        `/api/v1/balance-trx-payments/${payment.id}/-actions/verify`,
        {
          method: 'patch',
        }
      );

      if (err) {
        this.createAlert({
          data: 'Gagal memverifikasi pembayaran',
          detail: this.getRequestErrorMessage(err),
          status: 'error',
        });
      } else {
        this.createAlert({
          data: 'Pembayaran berhasil diverifikasi',
          status: 'success',
        });

        this.loadPayments();

        this.balanceTrx.data.attributes.paid = res.data.attributes.trx_paid;
      }

      this.loadingVerify = false;
    },
    onOpenUploadDocument(payment) {
      this.uploadDocument.id = payment.id;
      this.uploadDocument.visible = true;
    },
    onChangeUploadDocument(e) {
      this.uploadDocument.file = e.target.files[0];
    },
    async onUploadDocument() {
      this.uploadDocument.loading = true;

      const data = new FormData();

      data.append('file', this.uploadDocument.file);

      const [, err] = await this.request(
        `/api/v1/balance-trx-payments/${this.uploadDocument.id}/-actions/upload-document`,
        {
          method: 'post',
          data,
        }
      );

      if (err) {
        this.createAlert({
          data: 'Gagal mengupload bukti pembayaran',
          detail: this.getRequestErrorMessage(err),
          status: 'error',
        });
      } else {
        this.loadPayments();

        this.uploadDocument.visible = false;
      }

      this.uploadDocument.loading = false;
    },
    onOpenPaymentQr(payment) {
      this.paymentQris.visible = true;
      this.paymentQris.qris = payment.attributes.qris;
      this.paymentQris.qris_merchant = payment.attributes.qris_merchant;
      this.paymentQris.qris_city = payment.attributes.qris_city;
      this.paymentQris.bank_account_name =
        payment.attributes.office_bank_account_name;
      this.paymentQris.bank_account_number =
        payment.attributes.office_bank_account_number;
      this.paymentQris.price = payment.attributes.amount;
      this.paymentQris.orderCode = this.balanceTrx.code;
    },
    async onEditPayment(payment) {
      this.loadingEditPayment = true;

      await this.request(`/api/v1/balance-trx-payments/${payment.id}`, {
        method: 'patch',
        data: {
          data: {
            type: 'balance-trx-payments',
            id: payment.id,
            attributes: {
              transfer_date: payment.attributes.transfer_date,
              receipt_number: payment.attributes.receipt_number,
            },
          },
        },
      });

      this.loadingEditPayment = false;
    },
  },
  created() {
    this.loadBalanceTrx();
    this.loadPayments();
  },
};
</script>
